import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AppointmentListItem } from '@/hooks/useAppointments';

interface MonthViewProps {
  anchor: Date;
  appointments: AppointmentListItem[];
  onDayClick: (date: Date) => void;
}

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

function startOfMonth(d: Date): Date {
  const date = new Date(d);
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return date;
}

function startOfWeekMonday(d: Date): Date {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  const dow = date.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  date.setDate(date.getDate() + offset);
  return date;
}

function addDays(d: Date, n: number): Date {
  const date = new Date(d);
  date.setDate(date.getDate() + n);
  return date;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function startOfDay(d: Date): Date {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  return date;
}

export function MonthView({ anchor, appointments, onDayClick }: MonthViewProps) {
  const { t, i18n } = useTranslation();
  const today = useMemo(() => startOfDay(new Date()), []);

  const days = useMemo(() => {
    const monthStart = startOfMonth(anchor);
    const gridStart = startOfWeekMonday(monthStart);
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [anchor]);

  const appointmentsByDay = useMemo(() => {
    const map = new Map<string, AppointmentListItem[]>();
    for (const apt of appointments) {
      const dt = new Date(apt.starts_at);
      const key = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(apt);
    }
    return map;
  }, [appointments]);

  function dayKey(d: Date) {
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  }

  function getDayAppointments(d: Date): AppointmentListItem[] {
    return appointmentsByDay.get(dayKey(d)) ?? [];
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-card-dark">
      {/* Weekday header row */}
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
        {WEEKDAY_KEYS.map((key) => (
          <div
            key={key}
            className="border-r border-gray-200 py-2 text-center text-xs font-medium text-gray-500 last:border-r-0 dark:border-gray-700 dark:text-gray-400"
          >
            {t(`appointments.weekdays_short.${key}`)}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const dayAppointments = getDayAppointments(day);
          const isToday = isSameDay(day, today);
          const isCurrentMonth = isSameMonth(day, anchor);

          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => onDayClick(day)}
              className={`group relative min-h-[100px] border-b border-r border-gray-200 p-2 text-left transition-colors last:border-r-0 hover:bg-indigo-50 dark:border-gray-700 dark:hover:bg-indigo-900/20 ${
                !isCurrentMonth ? 'bg-gray-50/50 dark:bg-gray-800/50' : ''
              }`}
            >
              <div className="mb-1 flex items-start justify-between">
                <span
                  className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-medium ${
                    isToday
                      ? 'bg-indigo-600 text-white'
                      : isCurrentMonth
                        ? 'text-gray-900 dark:text-white'
                        : 'text-gray-400 dark:text-gray-600'
                  }`}
                >
                  {day.getDate()}
                </span>
                {dayAppointments.length > 0 && (
                  <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-indigo-600 px-1.5 text-xs font-medium text-white">
                    {dayAppointments.length}
                  </span>
                )}
              </div>

              {/* Show up to 3 appointment indicators */}
              <div className="space-y-1">
                {dayAppointments.slice(0, 3).map((apt) => {
                  const start = new Date(apt.starts_at);
                  const timeStr = start.toLocaleTimeString(
                    i18n.language === 'da' ? 'da-DK' : 'en-US',
                    {
                      hour: '2-digit',
                      minute: '2-digit',
                    },
                  );
                  const visitStatusColors: Record<string, string> = {
                    checked_in:        '#3B82F6',
                    under_treatment:   '#F97316',
                    treatment_finished:'#22C55E',
                    payment_completed: '#A855F7',
                  };
                  const bgColor = apt.service_color || '#6366F1';
                  const borderColor = apt.visit_status ? visitStatusColors[apt.visit_status] : null;
                  return (
                    <div
                      key={apt.id}
                      className="flex items-center truncate rounded-sm px-1.5 py-0.5 text-xs text-white"
                      style={{ backgroundColor: bgColor, borderLeft: borderColor ? `3px solid ${borderColor}` : undefined }}
                    >
                      {timeStr} {apt.patient_name}
                    </div>
                  );
                })}
                {dayAppointments.length > 3 && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    +{dayAppointments.length - 3} {i18n.language === 'da' ? 'mere' : 'more'}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
